import { type Dispatch, type SetStateAction } from 'react';

/**
 * Types and interfaces for EntitlementReports components
 */

export interface EntReportPreviewProps {
  setShowReportModal: Dispatch<SetStateAction<boolean>>;
}

export interface EntReportTableProps {
  onColumnConfigChange?: (config: {
    selectedColumns: string[];
    columnOrder: Record<string, number>;
  }) => void;
}

export type SodEntReportData = Record<string, React.ReactElement>;

export type EntReportTabType =
  | 'INHERENT_ENTITLEMENT_RISK'
  | 'INHERENT_ENTITLEMENT_USER';

// API Types
export enum EntReportType {
  API_SLICE_NAME = 'entReportsApi',
}

export type EntReportData = Record<string, React.ReactElement>;

export interface EntReportDataRaw {
  id: string;
  name: string;
  type: string;
  status: string;
  createdDate: string;
  modifiedDate: string;
  [key: string]: unknown;
}

export interface GetReportColumnsApiResponse {
  status: number;
  message: string;
  data: unknown;
}

export interface GetEntReportPreviewRequest {
  reportType: string;
  filters?: Record<string, unknown>;
  columns?: string[];
  [key: string]: unknown;
}

export interface GetEntReportPreviewApiResponse {
  status: number;
  message: string;
  data: EntReportDataRaw[];
}

export interface CreateAsyncExportRequest {
  reportType: string;
  filters?: Record<string, unknown>;
  columns?: string[];
  format?: string;
  columnConfig?: {
    columnOrder: Record<string, number>;
    selectedColumns: string[];
    columnDisplay?: Record<string, string>;
  };
  [key: string]: unknown;
}

export interface CreateAsyncExportApiResponse {
  status: number;
  message: string;
  data: {
    exportId: string;
    status: string;
  };
}

/** Row from `/reports/exports/status` (same contract as Violations export status). */
export interface EntExportStatusItem {
  exportId: string;
  reportType: string;
  status: string;
  progress: number;
  progressPercentage?: number;
  createdDate?: string;
  errorMessage?: string;
  /** Server-suggested delay until the next status poll (ms). */
  nextPollingIntervalMs?: number;
}

export interface GetExportStatusData {
  userId?: string;
  totalExports?: number;
  inProgressExports?: number;
  completedExports?: number;
  failedExports?: number;
  exports: EntExportStatusItem[];
}

export interface GetExportStatusApiResponse {
  status: number;
  message: string;
  data: GetExportStatusData;
}

export interface CreateSyncExportRequest {
  reportType: string;
  format: string;
  columnConfig: {
    columnOrder: Record<string, number>;
    selectedColumns: string[];
    columnDisplay?: Record<string, string>;
  };
  filters: {
    entitlementValueKeys?: string[];
    [key: string]: unknown;
  };
}

/**
 * Converts a report type code (e.g., 'RISK_DETAILS', 'USER_DETAILS') to a user-friendly label.
 * @param reportType - The report type code to format
 * @returns Title-cased label for display
 */
export default function formatEntReportType(reportType: string): string {
  switch (reportType) {
    case 'RISK_DETAILS':
      return 'Entitlements with Inherent Risk';
    case 'INHERENT_ENTITLEMENT_RISK':
      return 'Entitlements with Inherent Risk';
    case 'USER_DETAILS':
      return 'Entitlements with Inherent Risk';
    case 'INHERENT_ENTITLEMENT_USER':
      return 'Entitlements with Inherent Risk';
    default:
      return reportType;
  }
}
